/**
 * The »seamless input« behavior seam (the CSS lives in {@link ./styles}).
 *
 * A seamless input is an auto-resizing multi-line `<textarea>` that resizes with
 * its content via the modern CSS `field-sizing: content`. The platform is
 * Chrome-first, so that is the whole mechanism on the happy path; for browsers
 * without `field-sizing` this module adds a TRIVIAL fallback: it reads the
 * textarea's OWN `scrollHeight` on input (no measurement mirror, no hidden clone)
 * and pins the height.
 *
 * The directive SETS `field-sizing` itself rather than assuming a stylesheet did.
 * It used to no-op under support, on the assumption that the property came from
 * the `.rlhf-seam` class — so every consumer that spread the directive onto a
 * textarea carrying its OWN class got no auto-resize at all on exactly the modern
 * browsers the fast path was written for, and a fixed `rows` box instead (found in
 * the Werte-Sektion's feedback composer on iOS Safari). Self-provisioning is what
 * the "spread it onto the textarea" contract already promised: correct regardless
 * of the consumer's CSS class.
 *
 * Exposed as a lit `ref` binding so elements just spread `${seamAutoGrow()}` onto
 * the textarea; the callback identity is stable, so re-renders never re-bind.
 */

import { ref } from "@platform/web-kit/render"

/**
 * True when the browser resizes textareas from content in pure CSS. Read at BIND time,
 * not at module load: a module-level constant froze the answer to whatever globals
 * happened to exist when the bundle first evaluated, which is the wrong moment in an
 * SSR/test DOM and untestable in both directions. One `CSS.supports` call per textarea
 * mount is not worth caching.
 */
function supportsFieldSizing(): boolean {
  if (typeof CSS === "undefined" || typeof CSS.supports !== "function") return true // no reliable detection → assume Chrome-first support
  return CSS.supports("field-sizing", "content")
}

/** Pin a textarea's height to its content using its own scrollHeight (no mirror). */
function grow(ta: HTMLTextAreaElement): void {
  ta.style.height = "auto"
  ta.style.height = `${ta.scrollHeight}px`
}

/**
 * Make one textarea auto-resize. Stable callback identity, so lit re-renders never
 * re-bind. Exported for callers outside a lit template (and so the two branches are
 * directly testable); inside a template use {@link seamAutoGrow}.
 */
export const applySeamAutoGrow = (el?: Element): void => {
  if (!el) return
  const ta = el as HTMLTextAreaElement
  if (supportsFieldSizing()) {
    ta.style.setProperty("field-sizing", "content")
    return
  }
  grow(ta)
  ta.addEventListener("input", () => grow(ta))
}

/**
 * lit `ref` binding for a seamless textarea — spread onto the element:
 * `<textarea ${seamAutoGrow()} …>`. Auto-resizes regardless of the element's class.
 */
export function seamAutoGrow() {
  return ref(applySeamAutoGrow)
}
